import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoBagHandleOutline } from "react-icons/io5";
import defaultAvatar from "../../assets/profile.png";
import { authHeaders } from "../becomesell/LiveShoppingParts";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
// The active-lives API has no realtime feed, so the list is refreshed while the page is open
const LIVE_SELLERS_REFRESH_MS = 60000;

export const liveStreamPath = (stream) =>
  stream.stream_type === "marketplace" ? `/live-shopping/${stream.stream_id}` : `/stream/${stream.stream_id}`;

// Marketplace lives of sellers the viewer may watch; null until the first load
export const useLiveSellers = () => {
  const [streams, setStreams] = useState(null);

  useEffect(() => {
    let active = true;
    const load = () =>
      fetch(`${API_BASE_URL}/live/active-users?filter=sellers&stream_type=marketplace`, { headers: authHeaders() })
        .then((res) => res.json())
        .then((data) => active && setStreams(data?.success ? data.result?.streams || [] : []))
        .catch(() => active && setStreams((prev) => prev || []));
    load();
    const timer = setInterval(load, LIVE_SELLERS_REFRESH_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  return streams;
};

// Sellers currently live with a featured product
export const LiveShopColumn = () => {
  const navigate = useNavigate();
  const streams = useLiveSellers();

  return (
    <aside className="min-w-0">
      <h3 className="text-center font-semibold text-[#161F28] mb-4">Watch Now &amp; Shop Live</h3>
      {streams === null ? (
        <p className="text-center text-sm text-gray-500">Loading...</p>
      ) : streams.length === 0 ? (
        <p className="text-center text-sm text-gray-500">No sellers are live right now.</p>
      ) : (
        <div className="space-y-4">
          {streams.slice(0, 4).map((stream) => {
            const product = stream.user?.seller?.featuredProduct;
            const sellerName = stream.user?.seller?.name || `${stream.user?.first_name || ""} ${stream.user?.last_name || ""}`.trim();
            const currencySymbol = stream.user?.seller?.currency_symbol || "$";
            return (
              <div key={stream.stream_id} className="rounded-2xl overflow-hidden shadow-md bg-[#FFFCE8]">
                <div
                  className="relative h-[150px] bg-gray-200 cursor-pointer"
                  onClick={() =>
                    navigate(liveStreamPath(stream))
                  }
                >
                  <img
                    src={product?.thumbnailUrl || stream.user?.profile_picture_url || defaultAvatar}
                    alt={stream.title || sellerName}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">LIVE</span>
                  <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-white/90 rounded-full pr-2">
                    <img
                      src={stream.user?.profile_picture_url || defaultAvatar}
                      alt={sellerName}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="text-[10px] font-semibold truncate max-w-[110px]">{sellerName}</span>
                  </div>
                  <span className="absolute top-10 left-2 text-white text-[10px] font-semibold drop-shadow">
                    {Number(stream.viewer_count) || 0} Viewers
                  </span>
                </div>
                {product && (
                  <div className="flex gap-3 p-3 -mt-8 relative">
                    <div className="relative w-[72px] h-[72px] bg-white rounded-lg shadow flex-shrink-0">
                      <img
                        src={product.thumbnailUrl || "/image/shoeimg.png"}
                        alt={product.name}
                        className="w-full h-full object-contain rounded-lg"
                      />
                      {Number(product.liveOfferPercentage ?? product.offerPercentage) > 0 && (
                        <span className="absolute -top-2 left-0 bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded">
                          {product.liveOfferPercentage ?? product.offerPercentage}% Offer
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 pt-8">
                      <p className="text-[11px] font-semibold uppercase line-clamp-2">{product.name}</p>
                      <p className="text-sm font-bold text-[#095228]">{currencySymbol}{Number(product.livePrice ?? (product.discountedPrice || product.price) ?? 0).toFixed(2)}</p>
                      <button
                        type="button"
                        onClick={() => navigate(liveStreamPath(stream))}
                        className="mt-1 flex items-center gap-1 bg-[#095228] text-white text-[10px] font-semibold px-3 py-1 rounded-full"
                      >
                        <IoBagHandleOutline /> JOIN NOW
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </aside>
  );
};

export default LiveShopColumn;
